// apps/client/src/hooks/useCompanyData.js
import { useEffect, useState, useRef } from 'react';
import { doc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useUserData } from './useUserData';
import { useAuth } from '../context/AuthContext';

interface CompanyData {
  id: string;
  name: string;
  companyName: string;
  featureFlags?: Record<string, any>;
  [key: string]: any;
}

interface Office {
  id: string;
  [key: string]: any;
}

interface TeamMember {
  id: string;
  displayName?: string;
  [key: string]: any;
}
/**
 * Loads company, offices, and team members for the current user.
 * - Guards against setState after unmount (isActive flag)
 * - Normalises name so BOTH `name` and `companyName` are present
 * - Merges subscription subcollection data into companyData.subscription
 * - Returns { companyData, offices, teamMembers, loading, error }
 */
export const useCompanyData = () =>{
  const { userData } = useUserData();
  const { companyId: claimsCompanyId, claims } = useAuth();
  // Prefer auth-claims companyId so founder view-as sessions fetch the
  // viewAs target tenant's company data, NOT the founder's own ('system').
  // The founder's verified ID-token claim is the security source of truth;
  // userData.companyId is a Firestore mirror that doesn't reflect the
  // viewAs override. Falls back to userData.companyId for non-viewAs
  // sessions where the two values are identical.
  const companyId = claimsCompanyId || userData?.companyId || null;
  // Marketing can't list /users (admin-only rule), so it sources team labels
  // from the gated getBrokerLabels callable instead of a direct collection read.
  const isServiceStaff = claims?.role === 'service_staff' || userData?.role === 'service_staff';
  const isMarketing = userData?.role === 'marketing';

  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [offices, setOffices] = useState<Office[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Refs to hold latest data from each listener so merge is race-condition-safe
  const companyDocRef = useRef<CompanyData | null>(null);
  const subscriptionRef = useRef<Record<string, any> | null>(null);
  const subscriptionLoadedRef = useRef(false);
  const usersLoadedRef = useRef(false);

  useEffect(() => {
    // Reset when company changes / user logs out
    if (!companyId) {
      companyDocRef.current = null;
      subscriptionRef.current = null;
      setCompanyData(null);
      setOffices([]);
      setTeamMembers([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (isServiceStaff) {
      let active = true;
      setLoading(true);
      setError(null);
      setOffices([]);
      setTeamMembers([]);
      httpsCallable<{ action: string; companyId: string }, CompanyData>(functions, 'leadCaptureAction')({ action: 'getLeadStaffContext', companyId })
        .then(result => { if (active) setCompanyData(result.data); })
        .catch((err: Error) => { if (active) { setCompanyData(null); setError(err); } })
        .finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
    }

    setLoading(true);
    setError(null);
    subscriptionLoadedRef.current = false;
    usersLoadedRef.current = false;

    let isActive = true;

    // Only set loading=false when BOTH users and subscription listeners have fired
    const maybeFinishLoading = () => {
      if (usersLoadedRef.current && subscriptionLoadedRef.current) {
        setLoading(false);
      }
    };

    // Merge company doc + subscription data and update state
    const mergeAndSet = () => {
      if (!isActive || !companyDocRef.current) return;
      const merged = { ...companyDocRef.current };
      if (subscriptionRef.current) {
        merged.subscription = subscriptionRef.current;
      }
      setCompanyData(merged);
    };

    // --- Company doc subscription
    const unsubscribeCompany = onSnapshot(
      doc(db, 'companies', companyId),
      (snap: any) => {
        if (!isActive) return;

        if (snap.exists()) {
          const raw = snap.data() || {};
          // NORMALISE: prefer `companyName`, then `name`
          const normalisedName = raw.companyName ?? raw.name ?? '';
          companyDocRef.current = {
            id: snap.id,
            ...raw,
            name: normalisedName,
            companyName: normalisedName,
          };
          mergeAndSet();
        } else {
          companyDocRef.current = null;
          setCompanyData(null);
        }
      },
      (err: any) => {
        if (!isActive) return;
        setError(err);
        companyDocRef.current = null;
        setCompanyData(null);
      }
    );

    // --- Offices collection subscription
    const unsubscribeOffices = onSnapshot(
      collection(db, `companies/${companyId}/offices`),
      (snap: any) => {
        if (!isActive) return;
        setOffices(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
      },
      (err: any) => {
        if (!isActive) return;
        setError(err);
        setOffices([]);
      }
    );

    // --- Subscription config subscription (subcollection: companies/{id}/subscription/config)
    const unsubscribeSubscription = onSnapshot(
      doc(db, 'companies', companyId, 'subscription', 'config'),
      (snap: any) => {
        if (!isActive) return;
        subscriptionRef.current = snap.exists() ? snap.data() : null;
        subscriptionLoadedRef.current = true;
        mergeAndSet();
        maybeFinishLoading();
      },
      (err: any) => {
        // Subscription read may fail for Firestore rules — degrade gracefully to 'plus' tier
        if (!isActive) return;
        console.warn('Subscription snapshot error (falling back to plus tier):', err);
        subscriptionLoadedRef.current = true;
        maybeFinishLoading();
      }
    );

    // --- Team members ---
    // Marketing: list /users is admin-only, so fetch broker labels via the gated
    // getBrokerLabels callable (id/displayName/officeIds only — no colleague
    // email/profile/claims). One-shot (roster changes rarely); failures are
    // non-fatal (pickers degrade to empty rather than blocking the shell).
    // Everyone else keeps the realtime /users subscription.
    let unsubscribeUsers: () => void = () => {};
    if (isMarketing) {
      const getBrokerLabels = httpsCallable(functions, 'getBrokerLabels');
      getBrokerLabels({ companyId })
        .then((res: any) => {
          if (!isActive) return;
          const brokers = (res?.data?.brokers ?? []) as Array<{
            id: string;
            displayName?: string;
            officeIds?: string[];
          }>;
          const list = brokers.map((b) => ({
            id: b.id,
            displayName: b.displayName,
            officeIds: b.officeIds ?? [],
          }));
          list.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
          setTeamMembers(list);
          usersLoadedRef.current = true;
          maybeFinishLoading();
        })
        .catch((err: any) => {
          if (!isActive) return;
          console.error('[useCompanyData] getBrokerLabels failed (non-fatal)', err);
          setTeamMembers([]);
          usersLoadedRef.current = true;
          maybeFinishLoading();
        });
    } else {
      const usersQ = query(collection(db, 'users'), where('companyId', '==', companyId));
      unsubscribeUsers = onSnapshot(
        usersQ,
        (snap: any) => {
          if (!isActive) return;
          const list = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
          list.sort((a: any, b: any) => (a.displayName || '').localeCompare(b.displayName || ''));
          setTeamMembers(list);
          usersLoadedRef.current = true;
          maybeFinishLoading();
        },
        (err: any) => {
          if (!isActive) return;
          setError(err);
          setTeamMembers([]);
          usersLoadedRef.current = true;
          maybeFinishLoading();
        }
      );
    }

    // Cleanup
    return () => {
      isActive = false;
      companyDocRef.current = null;
      subscriptionRef.current = null;
      unsubscribeCompany();
      unsubscribeSubscription();
      unsubscribeOffices();
      unsubscribeUsers();
    };
  }, [companyId, isMarketing, isServiceStaff]);

  return { companyData, offices, teamMembers, loading, error };
};
